import {
  AppBar,
  Button,
  Theme,
  Toolbar,
} from '@mui/material'
import { AppLogo } from 'components/Layout/AppLogo'
import { Container } from 'components/Layout/Container'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { SearchGlobal } from 'components/Search/SearchGlobal'
import { useAppRunningContext } from 'features/EmbeddedRh24/useAppRunningContext'
import { useLocation, useNavigate } from 'react-router'
import { RootAppState, useAppSelector } from 'store/configureStore'
import { makeStyles } from 'tss-react/mui'
import UserButton from '../../UserButton/UserButton'
import { NewProjectButton } from './NewProjectButton/NewProjectButton'

import { useAuth0 } from '@auth0/auth0-react'
import { useOrganizationContext } from 'utils/useOrganizationContext'

import { SocialMedia } from 'components/SocialMedia'
import {
  IncomingRfqsButton,
  OutgoingRfqsButton,
} from 'features/RequestForQuotations/RfqLinkButtons'
import { Hidden } from '../Hidden/Hidden'
import './NavBar.sass'

const useStyles = makeStyles({ name: 'navbar' })((theme: Theme) => ({
  toolbar: {
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1em',
    maxWidth: theme.breakpoints.values.xl,
    margin: '0 auto',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      minHeight: '62px',
      padding: 0,
    },
  },
  logoContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 0 auto',
    textAlign: 'center',
    '& button': {
      height: '100%',
      padding: 0,
    },
    '& button:hover': {
      backgroundColor: 'transparent',
    },
  },
  logoImg: {
    height: `100%`,
    maxHeight: '62px',
    maxWidth: theme.spacing(20),
    mixBlendMode: theme.palette.mode === 'dark' ? 'hard-light' : 'inherit',
  },
  search: {
    ['& .MuiInputBase-root']: {
      color: 'inherit',
      '& button': {
        color: 'inherit',
      },
    },
    flex: '1 1 auto',
    minWidth: 0,
  },
  fieldSection: {
    display: 'flex',
    height: '100%',
    flexShrink: 0,
  },
  sectionDesktop: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1em',
      flex: '1 1 auto',
      width: '100%',
    },
    display: 'flex',
  },
  sectionMobile: {
    display: 'flex',
    marginLeft: 'auto',
  },
  navBarContainer: {
    width: '100%',
    [theme.breakpoints.down('md')]: {
      padding: `${theme.spacing(0)} !important`,
    },
  },
}))

const MyProjectsButton = () => {
  const navigate = useNavigate()

  const { partyId } = useOrganizationContext()

  if (!partyId) return null

  return (
    <Button
      color="inherit"
      component="a"
      href="/app"
      variant="text"
      data-testid="navbar-btn-my-projects"
      // onAuxClick={() => window.open('/app', '_blank')}
      onClick={(e) => {
        e.preventDefault()

        if (e.ctrlKey || e.metaKey) return window.open('/app', '_blank')

        navigate('/app')

        return null
      }}
      style={{ flex: '0 0 auto' }}
    >
      <LocalizedTypography
        color="inherit"
        translationKey="common:my-projects"
        translationDefaultValue={'my projects'}
      />
    </Button>
  )
}

const NavBar = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { partyId: organizationId } = useOrganizationContext()
  // const currentModule = useCurrentAppModule()

  const { isAuthenticated } = useAuth0()
  const userProfile = useAppSelector(
    (state: RootAppState) => state.user.profile,
  )

  const { classes } = useStyles()

  const hasActiveProject = useAppSelector((state: RootAppState) =>
    Boolean(state.project.activeProject),
  )

  const { withinIframe } = useAppRunningContext()

  const hasOrganizationContext = organizationId

  if (!isAuthenticated || !userProfile) return null

  if (!location.pathname.includes('/app')) return null

  return (
    <AppBar position={'sticky'} color="transparent">
      <Container maxWidth="xl" className={classes.navBarContainer}>
        <Toolbar className={classes.toolbar}>
          {!withinIframe && (
            <Hidden lgUp>
              <div className="quotation-factory-logo">
                Q<span className="quotation-factory-logo-highlight">F</span>
              </div>
            </Hidden>
          )}
          <Hidden lgDown>
            <div className={classes.logoContainer}>
              <Button
                onMouseDown={(e) => {
                  switch (e.button) {
                    case 0:
                      // left click
                      e.preventDefault()
                      navigate('/app')
                      break
                    case 1:
                      // middle click
                      e.preventDefault()
                      if (!withinIframe) {
                        window.open('/app', '_blank')
                      } else {
                      }
                      break
                    default:
                      break
                  }
                }}
                disableRipple
                disableFocusRipple
                disableElevation
              >
                <AppLogo className={classes.logoImg} />
              </Button>
              {/* <Typography variant="caption">{currentModule}</Typography> */}
            </div>
          </Hidden>
          <div
            id="navbar-placeholder"
            className="navbar-placeholder"
            style={{
              flex: hasActiveProject ? '1 1 auto' : '0 0 0',
              minWidth: 0,
            }}
          ></div>
          {!withinIframe && !hasActiveProject ? (
            <Hidden lgDown>
              <div className={classes.fieldSection}>
                <SocialMedia />
              </div>
            </Hidden>
          ) : null}
          <div className={classes.search}>
            {isAuthenticated && (
              <>
                {
                  // bomitems search bar should not be show in project page
                  !hasActiveProject && hasOrganizationContext && (
                    <SearchGlobal />
                  )
                }
              </>
            )}
          </div>
          <div className={classes.fieldSection}>
            <Hidden smDown>
              <div className={classes.sectionDesktop}>
                {hasOrganizationContext && (
                  <>
                    <Hidden lgDown>
                      <IncomingRfqsButton />
                    </Hidden>
                    <Hidden lgDown>
                      <OutgoingRfqsButton />
                    </Hidden>
                    <Hidden lgDown>
                      <MyProjectsButton />
                    </Hidden>
                    <NewProjectButton />
                  </>
                )}
                {/* <Hidden mdDown> */}
                <UserButton />
                {/* </Hidden> */}
                {/* <Hidden mdUp>
                  <MobileMenu portalView={withinIframe} />
                </Hidden> */}
              </div>
            </Hidden>
            <Hidden smUp>
              <div className={classes.sectionMobile}>
                {/* <MobileMenu portalView={withinIframe} /> */}
                <UserButton />
              </div>
            </Hidden>
          </div>
        </Toolbar>
      </Container>
    </AppBar>
  )
}

export default NavBar
